// Lib
import React, { DetailedHTMLProps, HTMLAttributes, useCallback, useMemo } from 'react';
import { NodeViewProps } from '@tiptap/core';
import { getColorCssValue } from '../../../../client/element/utils/elementColorStyleUtils';
import { useSelector } from 'react-redux';
import { getElement } from '../../../../client/element/selectors/elementSelector';
import { getColor, getTitle } from '../../../elements/utils/elementPropertyUtils';
import useSyncInlineBoardLinkDetails from './useSyncInlineBoardLinkDetails';
import { InlineBoardLinkOptions } from './inlineBoardLinkConstants';

type StyleProps = DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>['style'];

const DefaultBoardIcon = () => <span className="board-icon" />;

const InlineBoardLinkResolvedContent: React.FC<NodeViewProps> = (props) => {
    const { node, extension, view, getPos, editor } = props;

    const {
        clickableLinksWhenEditable,
        BoardIconComponent = DefaultBoardIcon,
        dispatchFetchInlineBoardLinkDetails,
    } = extension.options as InlineBoardLinkOptions;

    useSyncInlineBoardLinkDetails({
        dispatchFetchInlineBoardLinkDetails,
        attrs: node.attrs,
        updateAttributes: props.updateAttributes,
    });

    const boardId = node.attrs.boardId || '';

    const boardColor = useSelector((state) => getColor(getElement(state, { elementId: boardId })));
    const boardTitle = useSelector((state) => getTitle(getElement(state, { elementId: boardId })));

    const renderedColor = boardColor || node.attrs.color;

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            const link = event.target as HTMLLinkElement;

            if (!link?.href) return;

            if (view.editable && !clickableLinksWhenEditable) {
                // Select the link node when clicked
                editor.chain().setNodeSelection(getPos()).focus().run();
                return;
            }

            // Open the link in a new tab
            window.open(link.href, '_blank');
        },
        [view.editable, clickableLinksWhenEditable],
    );

    // @ts-ignore CSS custom properties fail the type check
    const style: StyleProps = useMemo(() => ({ '--board-color': getColorCssValue(renderedColor) }), [renderedColor]);

    return (
        <a
            className="pill"
            href={node.attrs.url}
            tabIndex={-1}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={handleClick}
            data-board-id={boardId}
            data-permission-id={node.attrs.permissionId || undefined}
        >
            <span className="board-icon-wrapper" style={style}>
                <BoardIconComponent />
            </span>
            <span className="board-title">{boardTitle || node.attrs.title || 'Unknown board'}</span>
        </a>
    );
};

export default InlineBoardLinkResolvedContent;
